/**
 * Drawer — the right-side slide-over primitive (UX-11 PR-1, the
 * clinical-drawer chrome).
 *
 * LOCKED CONTRACT (CTO gate 2026-07-02; the Data-protection pillar's
 * clinical encounter/document drawer composes on this): Drawer carries
 * NO capability predicate and NO tenant assumption — access gates are
 * computed by the composing feature and passed down into its content.
 * No data access and no PHI-relevant behavior (logging, fetching) lives
 * here; the composing feature's repos/interceptors own that. Drawer is
 * presentation chrome only: scrim, positioned panel, dialog a11y
 * (useDialogDismiss: focus-on-open, Esc, Tab trap, restore-focus),
 * and opt-in left-edge resize with per-browser width persistence.
 * Do not add gates or data concerns here without a new CTO gate.
 *
 * Extracted verbatim from WorkItemDrawer's chrome (Slice 3b-2 resize +
 * PR4a dismiss behavior); the width storage key is parameterized so
 * independent drawers (workspace vs future clinical) never share widths.
 *
 * PINNED FOOTER (design pass 2026-07-25, soak r2 punch item 1): a drawer
 * may pin a footer region below its scrolling body. Without one the
 * panel scrolls as a single column, exactly as before (the default path
 * is byte-identical). WITH one, the panel stops scrolling itself and the
 * composing feature's own children own the split — the feature marks its
 * header `shrink-0` and its body `flex-1 min-h-0 overflow-auto`. This
 * stays presentation chrome: no gate, no data, no PHI behavior.
 */

import { useEffect, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { useDialogDismiss } from '@/components/primitives/useDialogDismiss';
import { OverlayPortal } from '@/components/primitives/OverlayPortal';

export interface DrawerProps {
  onClose: () => void;
  ariaLabel: string;
  /** data-testid on the panel. */
  testId?: string;
  /**
   * Opt-in resizable width, persisted per browser under this key
   * (e.g. 'peaceos.workspace.drawerWidth'). Omit for a fixed-width drawer
   * (no resize handle).
   */
  widthStorageKey?: string;
  minWidth?: number;
  maxWidth?: number;
  defaultWidth?: number;
  /**
   * A region pinned below the scrolling body (never scrolls away). When
   * present the panel itself stops scrolling, so the composing feature's
   * children must carry their own `shrink-0` / `flex-1 min-h-0
   * overflow-auto` split.
   */
  footer?: ReactNode;
  children: ReactNode;
}

function readStoredWidth(key: string, min: number, max: number, fallback: number): number {
  if (typeof window === 'undefined' || !window.localStorage) return fallback;
  try {
    const n = parseInt(window.localStorage.getItem(key) ?? '', 10);
    return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : fallback;
  } catch {
    return fallback;
  }
}

export function Drawer({
  onClose,
  ariaLabel,
  testId,
  widthStorageKey,
  minWidth = 380,
  maxWidth = 760,
  defaultWidth = 440,
  footer,
  children,
}: DrawerProps): ReactNode {
  const dialogRef = useDialogDismiss<HTMLElement>(onClose);
  const [width, setWidth] = useState<number>(() =>
    widthStorageKey
      ? readStoredWidth(widthStorageKey, minWidth, maxWidth, defaultWidth)
      : defaultWidth,
  );

  // Persist the resized width (the user's own browser; jsdom-safe).
  useEffect(() => {
    if (!widthStorageKey) return;
    try {
      window.localStorage?.setItem(widthStorageKey, String(width));
    } catch {
      /* ignore */
    }
  }, [width, widthStorageKey]);

  // Drag the left edge to resize. The drawer is right-anchored, so width grows
  // as the pointer moves left (window.innerWidth - clientX), clamped to bounds.
  function startResize(e: ReactPointerEvent): void {
    e.preventDefault();
    const onMove = (ev: PointerEvent): void => {
      setWidth(Math.min(maxWidth, Math.max(minWidth, window.innerWidth - ev.clientX)));
    };
    const onUp = (): void => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }

  return (
    <OverlayPortal>
      <div className="fixed inset-0 z-40 bg-sage-800/30" onClick={onClose} aria-hidden="true" />
      <aside
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-label={ariaLabel}
        data-testid={testId}
        style={{ width }}
        className={
          'fixed inset-y-0 right-0 z-50 max-w-[92vw] bg-cream shadow-sh3 flex flex-col focus:outline-none ' +
          (footer ? 'overflow-hidden' : 'overflow-auto')
        }
      >
        {widthStorageKey && (
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize drawer"
            data-testid="drawer-resize"
            onPointerDown={startResize}
            className="absolute inset-y-0 left-0 w-1.5 cursor-col-resize hover:bg-sage-200/60"
          />
        )}
        {children}
        {footer && (
          <div data-testid={testId ? `${testId}-footer` : undefined} className="shrink-0">
            {footer}
          </div>
        )}
      </aside>
    </OverlayPortal>
  );
}
